import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import { 
  FileText, AlignLeft, Settings, Copy, Download, Zap, 
  Edit3, Trash2, CheckCircle, RefreshCw, Maximize2, ArrowLeft, Mail, Sparkles
} from 'lucide-react';


const ColdEmailGenerate = () => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [enhancingPrompt, setEnhancingPrompt] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form state
  const [recipientName, setRecipientName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [keyDetails, setKeyDetails] = useState("");
  const [tone, setTone] = useState("professional");
  const [customPrompt, setCustomPrompt] = useState("");
  const [enhancedPrompt, setEnhancedPrompt] = useState("");
  const [isCustomPromptEdited, setIsCustomPromptEdited] = useState(false);

  // User data
  const [credits, setCredits] = useState(0);

  // Generated email
  const [generatedEmail, setGeneratedEmail] = useState(null);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);

  // Tone options
  const toneOptions = [
    { value: "friendly", label: "Friendly" },
    { value: "formal", label: "Formal" },
    { value: "persuasive", label: "Persuasive" },
    { value: "promotional", label: "Promotional" },
    { value: "casual", label: "Casual" },
    { value: "professional", label: "Professional" },
    { value: "apologetic", label: "Apologetic" },
    { value: "appreciative", label: "Appreciative" },
  ];

  // Fetch API keys from endpoint
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) {
          setError("No API keys available. Please contact administrator.");
        }
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Get random API key
  const getRandomApiKey = () => {
    if (apiKeys.length === 0) {
      throw new Error("No API keys available");
    }
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Fetch user data and API keys when user is authenticated
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Initialize custom prompt on component mount
  useEffect(() => {
    setCustomPrompt(
      "Generate a personalized email template based on the provided information."
    );
  }, []);

  // Construct the AI prompt
  const constructPrompt = () => {
    const basePrompt = `You are an AI-powered Email Template Generator. Your task is to create personalized, professional email templates based on the user's inputs.
    
    The user has provided:
    - Recipient Name: ${recipientName}
    - Sender Name: ${senderName}
    - Purpose of the Email: ${purpose}
    - Key Details or Hints: ${keyDetails}
    - Desired Tone: ${tone}
    
    Your output must:
    1. Include a subject line.
    2. Start with a personalized greeting using the recipient's name.
    3. Write a clear, engaging, and professional email body (120–200 words).
    4. Incorporate the purpose and hints naturally (avoid generic/robotic tone).
    5. End with a clear closing and the sender's name.
    6. Ensure the email is ready-to-send without requiring edits.
    
    Format the response like this:
    Subject: [Generated Subject Line]  
    Body:  
    [Generated Email Body]`;

    // Add the custom prompt or enhanced prompt
    return `${basePrompt} ${enhancedPrompt || customPrompt}`;
  };

  // Enhance the prompt using gemini-2.5-flash-lite model
  const enhancePrompt = async () => {
    if (!customPrompt.trim()) {
      setError("Please enter a custom prompt to enhance");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    setEnhancingPrompt(true);
    setError("");
    setSuccess("");
    try {
      const MODEL_ID = "gemini-2.5-flash-lite";
      const GENERATE_CONTENT_API = "generateContent";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:${GENERATE_CONTENT_API}?key=${apiKey}`;

      // Use the current custom prompt in the enhancement request
      const enhancePromptText = `Enhance this email generation prompt for better results: "${customPrompt}". 
      Make it more descriptive and effective for generating high-quality, personalized email templates. 
      The enhanced prompt should focus on creating professional, engaging emails that match the specified tone and purpose.
      Consider these details in your enhancement:
      - Recipient: ${recipientName || "[Not specified]"}
      - Sender: ${senderName || "[Not specified]"}
      - Purpose: ${purpose || "[Not specified]"}
      - Key Details: ${keyDetails || "[Not specified]"}
      - Tone: ${tone}`;

      const requestBody = {
        contents: [
          {
            role: "user",
            parts: [
              {
                text: enhancePromptText,
              },
            ],
          },
        ],
        generationConfig: {
          thinkingConfig: {
            thinkingBudget: 0,
          },
        },
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const enhancedText = data.candidates[0].content.parts[0].text;
        setEnhancedPrompt(enhancedText);
        setSuccess("Prompt enhanced successfully!");
      } else {
        throw new Error("No enhancement received from API");
      }
    } catch (err) {
      console.error("Error enhancing prompt:", err);
      setError(`Error enhancing prompt: ${err.message}`);
    } finally {
      setEnhancingPrompt(false);
    }
  };

  // Generate an email template
  const generateEmail = async () => {
    if (!recipientName.trim() || !senderName.trim() || !purpose.trim()) {
      setError("Recipient name, sender name, and purpose are required");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < 1) {
      setError(
        "Insufficient credits. You need at least 1 credit to generate an email template."
      );
      return;
    }

    setGenerating(true);
    setError("");
    setSuccess("");

    try {
      // Deduct 1 credit
      const deductResponse = await axios.post(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );

      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }

      // Update credits locally
      setCredits((prev) => prev - 1);

      // Generate email
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      let emailData = null;

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;

        // Parse the response to extract subject and body
        let subject = "";
        let body = "";

        // Check if the response contains the expected format
        if (
          generatedText.includes("Subject:") &&
          generatedText.includes("Body:")
        ) {
          // Split the text at the first occurrence of "Body:"
          const parts = generatedText.split("Body:");
          if (parts.length >= 2) {
            // Extract subject from the first part
            const subjectPart = parts[0];
            const subjectMatch = subjectPart.match(/Subject:\s*(.+?)(?=\n|$)/);
            if (subjectMatch) {
              subject = subjectMatch[1].trim();
            }

            // The rest is the body (join all parts after the first in case "Body:" appears in the content)
            body = parts.slice(1).join("Body:").trim();
          }
        } else {
          // Fallback if the format isn't as expected
          // Try to extract subject from the first line
          const lines = generatedText.split("\n");
          if (lines.length > 0 && lines[0].toLowerCase().includes("subject:")) {
            subject = lines[0].replace(/^Subject:\s*/i, "").trim();
            body = lines.slice(1).join("\n").trim();
          } else {
            // If no subject line, use a default
            subject = `Email from ${senderName}`;
            body = generatedText;
          }
        }

        // Clean up body - remove any remaining "Subject:" or "Body:" tags
        body = body
          .replace(/^Subject:\s*.+$/m, "")
          .replace(/^Body:\s*/m, "")
          .trim();

        emailData = {
          subject: subject,
          body: body,
          prompt: prompt,
          timestamp: new Date().toISOString(),
        };
      }

      if (!emailData) {
        console.log("Full API response:", JSON.stringify(data, null, 2));
        throw new Error("No email was generated from the API response");
      }

      setGeneratedEmail(emailData);
      setSuccess("Email template generated successfully!");
    } catch (err) {
      console.error("Error generating email:", err);
      setError(`Error: ${err.message}`);

      // Refund credits on failure
      try {
        await axios.post(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSuccess(`${type} copied to clipboard!`);
      })
      .catch((err) => {
        setError(`Failed to copy ${type.toLowerCase()} to clipboard`);
        console.error("Error copying text: ", err);
      });
  };

  // Copy the subject to clipboard
  const copySubjectToClipboard = () => {
    if (!generatedEmail) return;
    copyToClipboard(generatedEmail.subject, "Subject");
  };

  // Copy the body to clipboard
  const copyBodyToClipboard = () => {
    if (!generatedEmail) return;
    copyToClipboard(generatedEmail.body, "Body");
  };

  // Copy the entire email to clipboard
  const copyEmailToClipboard = () => {
    if (!generatedEmail) return;
    const emailText = `Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
    copyToClipboard(emailText, "Email");
  };

  // Download the generated email as a text file
  const downloadEmail = () => {
    if (!generatedEmail) return;

    const emailText = `Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
    const blob = new Blob([emailText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `email-template-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Buy more credits
  const buyMoreCredits = () => {
    alert("Redirecting to payment page...");
  };

  // Reset form
  const resetForm = () => {
    setRecipientName("");
    setSenderName("");
    setPurpose("");
    setKeyDetails("");
    setTone("professional");
    setCustomPrompt(
      "Generate a personalized email template based on the provided information."
    );
    setEnhancedPrompt("");
    setIsCustomPromptEdited(false);
    setGeneratedEmail(null);
    setError("");
    setSuccess("");
  };

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Please log in to use the Email Template Generator</h2>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Main Content */}
      <div className="container py-5 position-relative z-1">
        
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Cold Email <span className="text-neon">Generator</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={buyMoreCredits}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}
        {success && <div className="alert alert-success glass-alert" style={{backgroundColor: 'rgba(22, 163, 74, 0.2)', borderColor: '#16a34a', color: '#86efac'}}>{success}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Edit3 size={18} className="text-neon" />
                  Email Settings
                </h5>
              </div>
              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Recipient Name *</label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="form-control input-group-glass"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Sender Name *</label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="e.g. Jane Smith"
                      className="form-control input-group-glass"
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-light opacity-75">Purpose of Email *</label>
                  <input
                    type="text"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    placeholder="e.g. Meeting request, Thank you note"
                    className="form-control input-group-glass"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label text-light opacity-75">Key Details or Hints</label>
                  <textarea
                    value={keyDetails}
                    onChange={(e) => setKeyDetails(e.target.value)}
                    placeholder="Add specific details to include in the email"
                    rows={2}
                    className="form-control input-group-glass"
                    style={{resize: 'none'}}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label text-light opacity-75">Desired Tone</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="form-select input-group-glass"
                  >
                    {toneOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label text-light opacity-75">Custom Prompt</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => {
                      setCustomPrompt(e.target.value);
                      setIsCustomPromptEdited(true);
                    }}
                    placeholder="Add any specific instructions for the AI..."
                    rows={2}
                    className="form-control input-group-glass mb-2"
                    style={{resize: 'none'}}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={enhancePrompt}
                    disabled={enhancingPrompt || !customPrompt.trim() || apiKeys.length === 0}
                  >
                    {enhancingPrompt ? (
                      <><RefreshCw className="spin" size={14} /> Enhancing...</>
                    ) : (
                      <><Sparkles size={14} className="text-warning" /> Enhance Prompt</>
                    )}
                  </button>
                </div>

                {enhancedPrompt && (
                  <div className="mb-3">
                    <label className="form-label text-light opacity-75">Enhanced Prompt</label>
                    <textarea
                      value={enhancedPrompt}
                      onChange={(e) => setEnhancedPrompt(e.target.value)}
                      rows={2}
                      className="form-control input-group-glass border-warning"
                      style={{resize: 'none'}}
                    />
                  </div>
                )}

                <div className="d-flex gap-2 mt-auto pt-3 border-top border-secondary border-opacity-25">
                  <button
                    type="button"
                    className="btn btn-neon-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                    onClick={generateEmail}
                    disabled={
                      generating ||
                      !recipientName.trim() ||
                      !senderName.trim() ||
                      !purpose.trim() ||
                      credits < 1 ||
                      apiKeys.length === 0
                    }
                  >
                    {generating ? (
                      <><RefreshCw className="spin" size={18} /> Generating...</>
                    ) : (
                      <><Mail size={18} /> Generate Email</>
                    )}
                  </button>
                  <button type="button" className="btn btn-outline-light d-flex align-items-center justify-content-center" onClick={resetForm} title="Reset Form">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <CheckCircle size={18} className={generatedEmail ? "text-success" : "text-light opacity-50"} /> 
                  Generated Email
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {generatedEmail && (
                    <>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={copyEmailToClipboard} title="Copy Entire Email">
                        <Copy size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={downloadEmail} title="Download">
                        <Download size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "500px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="fs-5">AI is generating your email template...</p>
                  </div>
                ) : generatedEmail ? (
                  <div className="d-flex flex-column gap-4 h-100">
                    <div className="glass-card p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom border-secondary border-opacity-25 pb-2">
                        <h6 className="text-light opacity-75 m-0 text-uppercase fw-bold">Subject</h6>
                        <button className="btn btn-sm btn-link text-light opacity-75 p-0" onClick={copySubjectToClipboard}>
                          <Copy size={14} />
                        </button>
                      </div>
                      <h4 className="text-white m-0">{generatedEmail.subject}</h4>
                    </div>

                    <div className="glass-card p-4 flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom border-secondary border-opacity-25 pb-2">
                        <h6 className="text-light opacity-75 m-0 text-uppercase fw-bold">Body</h6>
                        <button className="btn btn-sm btn-link text-light opacity-75 p-0" onClick={copyBodyToClipboard}>
                          <Copy size={14} />
                        </button>
                      </div>
                      <div className="text-white" style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>
                        {generatedEmail.body}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <Mail size={64} className="mb-3" />
                    <p className="fs-5">Your generated email template will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      
    </div>
  );
};

export default ColdEmailGenerate;
